/*
 * "Ørepynt inkludert" offer. Spec: docs/inkludert-pdp-spec.md. Rules: docs/inkludert-spec.md.
 *
 * Two jobs:
 *   1. PDP: keep the nudge under the variant picker in the right state (below / included) and
 *      open the detail modal. Both are no-ops on pages where the nudge is absent.
 *   2. Cart reconciler, every page: after any cart update, make sure exactly one gift line
 *      (the Estelle studs in the ring's metal, quantity 1, tagged with the gift property) exists
 *      when a qualifying ring is in the cart, and none exists otherwise. Shopify's automatic
 *      Buy X Get Y discounts then zero that line; this file never touches discounts.
 *
 * Every ID and handle comes from the JSON block #KardimaGiftConfig
 * (snippets/kardima-gift-config.liquid). Nothing store-specific is hardcoded here.
 *
 * Event names: PUB_SUB_EVENTS is a top-level const in constants.js, so it is reachable from
 * this classic script when that file has loaded; the string literals are the fallback.
 */
(function () {
  'use strict';

  const EVENTS =
    typeof PUB_SUB_EVENTS !== 'undefined'
      ? PUB_SUB_EVENTS
      : { variantChange: 'variant-change', cartUpdate: 'cart-update' };
  const SOURCE = 'kardima-gift';

  function readConfig() {
    const el = document.getElementById('KardimaGiftConfig');
    if (!el) return null;
    try {
      const config = JSON.parse(el.textContent);
      if (!config || !Array.isArray(config.qualifying_handles) || !config.gift_variants) return null;
      return config;
    } catch (error) {
      return null;
    }
  }

  /** "2.5ct." -> 2.5, "2,5 ct" -> 2.5, anything else -> NaN. */
  function parseCarat(value) {
    if (typeof value !== 'string') return NaN;
    const match = value.trim().match(/^(\d+(?:[.,]\d+)?)/);
    return match ? parseFloat(match[1].replace(',', '.')) : NaN;
  }

  /** Option values use hyphens (Gult-Gull); copy uses spaces. Compare on a normalised form. */
  function normaliseKey(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .trim();
  }

  function fetchJson(url, options) {
    return fetch(url, options).then((response) => {
      if (!response.ok) throw new Error(`${url} ${response.status}`);
      return response.json();
    });
  }

  function postJson(url, body) {
    return fetchJson(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /* ── 1a. PDP nudge state ─────────────────────────────────────────────── */
  function initNudge(config) {
    if (!document.querySelector('[data-kardima-gift-nudge]')) return;
    if (typeof window.subscribe !== 'function') return;

    window.subscribe(EVENTS.variantChange, (event) => {
      // Re-query on every event: Dawn re-renders parts of product-info, never hold a stale node.
      const nudge = document.querySelector('[data-kardima-gift-nudge]');
      const variant = event && event.data && event.data.variant;
      if (!nudge || !variant || !Array.isArray(variant.options)) return;

      const caratIndex = parseInt(nudge.dataset.caratIndex, 10);
      if (Number.isNaN(caratIndex) || caratIndex < 0) return;

      const carat = parseCarat(variant.options[caratIndex]);
      if (Number.isNaN(carat)) return;

      const state = carat >= config.threshold ? 'included' : 'below';
      nudge.dataset.state = state;
      nudge.querySelectorAll('[data-gift-state]').forEach((el) => {
        el.hidden = el.dataset.giftState !== state;
      });
    });
  }

  /* ── 1b. PDP modal (mirrors kardima-size-guide.js) ───────────────────── */
  function initModal() {
    const modal = document.getElementById('KardimaGiftModal');
    const backdrop = document.querySelector('[data-kardima-gift-backdrop]');
    if (!modal || !backdrop) return;

    const panel = modal.querySelector('.kardima-gift-modal__panel');
    let lastFocused = null;

    function open(trigger) {
      lastFocused = trigger || document.activeElement;
      modal.hidden = false;
      backdrop.hidden = false;
      // Force layout so the transition has a starting frame to animate from.
      void panel.offsetWidth;
      modal.setAttribute('data-open', '');
      backdrop.setAttribute('data-open', '');
      document.body.classList.add('overflow-hidden');

      const close = modal.querySelector('[data-kardima-gift-close]');
      if (close) close.focus();
      document.addEventListener('keydown', onKeydown);
      modal.dispatchEvent(new CustomEvent('kardima:gift-modal-shown'));
    }

    function hide() {
      modal.removeAttribute('data-open');
      backdrop.removeAttribute('data-open');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', onKeydown);

      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        modal.hidden = true;
        backdrop.hidden = true;
        modal.dispatchEvent(new CustomEvent('kardima:gift-modal-hidden'));
        if (lastFocused && lastFocused.focus && document.contains(lastFocused)) lastFocused.focus();
      };
      // transitionend does not fire under prefers-reduced-motion, so always keep a fallback.
      panel.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400);
    }

    function onKeydown(event) {
      if (event.key === 'Escape') hide();
    }

    // Delegated: the trigger sits inside product-info, which Dawn may re-render.
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-kardima-gift-open]');
      if (!trigger) return;
      event.preventDefault();
      open(trigger);
    });
    modal.querySelectorAll('[data-kardima-gift-close]').forEach((el) => el.addEventListener('click', hide));
    backdrop.addEventListener('click', hide);

    // Clicking the dimmed area beside the panel closes too.
    modal.addEventListener('click', (event) => {
      if (!panel.contains(event.target)) hide();
    });

    initModalMedia(modal);
  }

  /* ── 1c. Modal media: photo swaps to the clip ────────────────────────── */
  /*
   * Hover starts the clip and leaving stops it, back to the photo. On touch there is no hover,
   * so the clip starts by itself when the modal opens (founder decision 2026-09-15: a phone
   * visitor should not have to find a tap target) and a tap pauses or resumes it.
   *
   * Reduced motion is the one case that never autoplays: those visitors get tap to start, and
   * nothing moves until they ask for it.
   *
   * play() rejects when the browser blocks it (a data saver, a codec it will not decode). The
   * catch leaves the photo showing rather than an empty box.
   */
  function initModalMedia(modal) {
    const media = modal.querySelector('[data-kardima-gift-media]');
    const video = media && media.querySelector('[data-kardima-gift-video]');
    if (!media || !video) return;

    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const tapToPlay = !canHover || reduced;

    function play() {
      const started = video.play();
      if (started && typeof started.catch === 'function') {
        started.catch(() => media.removeAttribute('data-playing'));
      }
      media.setAttribute('data-playing', '');
    }

    /* Leaving the photo, or closing the modal, rewinds. A tap on touch only pauses. */
    function stop() {
      pause();
      video.currentTime = 0;
    }

    function pause() {
      media.removeAttribute('data-playing');
      video.pause();
    }

    if (tapToPlay) {
      media.setAttribute('data-tap', '');
      media.addEventListener('click', (event) => {
        // The panel-level close handler must not see this as a click beside the panel.
        event.stopPropagation();
        if (media.hasAttribute('data-playing')) pause();
        else play();
      });
      // Touch, no reduced-motion preference: roll as soon as the modal is open.
      if (!reduced) modal.addEventListener('kardima:gift-modal-shown', play);
    } else {
      media.addEventListener('mouseenter', play);
      media.addEventListener('mouseleave', stop);
    }

    // A closed modal must never leave a video running in the background.
    modal.addEventListener('kardima:gift-modal-hidden', stop);
  }

  /* ── 2. Cart reconciler ──────────────────────────────────────────────── */
  function optionValue(item, names) {
    const list = Array.isArray(item.options_with_values) ? item.options_with_values : [];
    const hit = list.find((option) => names.includes(option.name));
    return hit ? hit.value : null;
  }

  function ringCarat(item, config) {
    let raw = optionValue(item, config.carat_option_names);
    if (raw == null && typeof item.variant_title === 'string') {
      // Fallback: the carat segment of "12 / Gult-Gull / 2.5ct." is the one that reads <number>ct.
      raw = item.variant_title.split('/').find((segment) => /^\s*\d+(?:[.,]\d+)?\s*ct/i.test(segment)) || null;
    }
    return parseCarat(raw);
  }

  function isGiftLine(item, config) {
    return Boolean(item.properties && String(item.properties[config.gift_property]) === '1');
  }

  function giftVariantForMetal(metal, config) {
    // Ring values may carry a karat ("18K Gult gull") while the studs are keyed "Gult-Gull",
    // so match on the colour word, never on the whole value.
    const wanted = normaliseKey(metal);
    const colour = wanted.includes('gult') ? 'gult' : wanted.includes('hvitt') ? 'hvitt' : null;
    if (!colour) return null;
    const key = Object.keys(config.gift_variants).find((k) => normaliseKey(k).includes(colour));
    return key ? Number(config.gift_variants[key]) : null;
  }

  /**
   * Minimal change set. Returns [] when the cart already matches the offer.
   * Steps run in order: removals first, then the single add, so a wrong-metal gift is never
   * momentarily doubled.
   */
  function buildPlan(cart, config) {
    const rings = cart.items.filter(
      (item) =>
        config.qualifying_handles.includes(item.handle) &&
        !isGiftLine(item, config) &&
        ringCarat(item, config) >= config.threshold
    );
    const gifts = cart.items.filter((item) => isGiftLine(item, config));

    let wantedId = null;
    if (rings.length) {
      wantedId = giftVariantForMetal(optionValue(rings[0], [config.metal_option_name]), config);
    }

    const plan = [];
    let keep = null;
    gifts.forEach((gift) => {
      if (wantedId && !keep && Number(gift.variant_id) === wantedId) {
        keep = gift;
        return;
      }
      plan.push({ op: 'change', key: gift.key, quantity: 0 });
    });
    if (keep && keep.quantity !== 1) plan.push({ op: 'change', key: keep.key, quantity: 1 });
    if (wantedId && !keep) plan.push({ op: 'add', id: wantedId });
    return plan;
  }

  function applyStep(step, config) {
    if (step.op === 'add') {
      const properties = {};
      properties[config.gift_property] = '1';
      return postJson(window.routes.cart_add_url, { items: [{ id: step.id, quantity: 1, properties }] });
    }
    return postJson(window.routes.cart_change_url, { id: step.key, quantity: step.quantity });
  }

  function sectionInner(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector || '.shopify-section');
    if (!node) throw new Error(`section selector not found: ${selector}`);
    return node.innerHTML;
  }

  /**
   * Re-render the way Dawn does after its own mutations: fetch the sections and swap them in.
   * The drawer's renderContents() also OPENS the drawer, which is right after an add but wrong
   * on page load, so a closed drawer gets the same swap without the open.
   */
  async function rerender(itemCount) {
    const cartUrl = window.routes.cart_url;
    const cartItems = document.querySelector('cart-items');
    const drawer = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');

    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      const sections = cartItems.getSectionsToRender();
      const html = await fetchJson(`${cartUrl}?sections=${sections.map((s) => s.section).join(',')}`);
      sections.forEach((section) => {
        const root = document.getElementById(section.id);
        if (!root || !html[section.section]) return;
        const target = root.querySelector(section.selector) || root;
        target.innerHTML = sectionInner(html[section.section], section.selector);
      });
      cartItems.classList.toggle('is-empty', itemCount === 0);
      if (cartFooter) cartFooter.classList.toggle('is-empty', itemCount === 0);
    }

    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      const sections = drawer.getSectionsToRender();
      const html = await fetchJson(`${cartUrl}?sections=${sections.map((s) => s.id).join(',')}`);
      drawer.classList.toggle('is-empty', itemCount === 0);
      if (drawer.classList.contains('active') && typeof drawer.renderContents === 'function') {
        drawer.renderContents({ sections: html });
      } else {
        sections.forEach((section) => {
          const el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
          if (!el || !html[section.id]) return;
          el.innerHTML = sectionInner(html[section.id], section.selector);
        });
        // The overlay lives inside #CartDrawer and was just replaced; Dawn's renderContents
        // re-binds its click-to-close the same way.
        const overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
      }
    }
  }

  function initReconciler(config) {
    if (!window.routes || !window.routes.cart_url) return;
    if (!document.querySelector('cart-drawer') && !document.querySelector('cart-items')) return;

    let running = false;
    let queued = false;

    async function reconcile() {
      const cart = await fetchJson(`${window.routes.cart_url}.js`);
      if (!cart || !Array.isArray(cart.items)) return;

      const plan = buildPlan(cart, config);
      if (!plan.length) return;

      for (const step of plan) {
        await applyStep(step, config);
      }

      const after = await fetchJson(`${window.routes.cart_url}.js`);
      try {
        await rerender(after ? after.item_count : cart.item_count);
      } catch (error) {
        // Documented fallback: a failed partial render is worse than a reload.
        window.location.reload();
        return;
      }

      // Let Dawn's own subscribers (cart-items, cart-drawer-items) refresh from the final state.
      // Tagged with our source so the handler below ignores it.
      if (typeof window.publish === 'function') {
        window.publish(EVENTS.cartUpdate, { source: SOURCE, cartData: after });
      }
    }

    // Re-entrancy: one run at a time; an event during a run queues exactly one more pass.
    function run() {
      if (running) {
        queued = true;
        return;
      }
      running = true;
      reconcile()
        .catch((error) => {
          if (window.console && console.warn) console.warn('[kardima-gift] reconcile failed', error);
        })
        .finally(() => {
          running = false;
          if (queued) {
            queued = false;
            run();
          }
        });
    }

    if (typeof window.subscribe === 'function') {
      window.subscribe(EVENTS.cartUpdate, (event) => {
        if (event && event.source === SOURCE) return;
        run();
      });
    }

    // Initial pass. Skipped when the header bubble says the cart is empty (no line to reconcile),
    // which spares one request per page view. Runs when the bubble markup is not on the page.
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && !bubble.querySelector('.cart-count-bubble')) return;
    run();
  }

  function init() {
    const config = readConfig();
    if (!config) return;
    initNudge(config);
    initModal();
    initReconciler(config);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
